import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Button } from "../../../../../../components/Button";
import { RetryButton } from "../../../../../../components/RetryButton";
import { noteTypes } from "../../../../../../constants/noteTypes";
import {
  getNoteData,
  textTruncate,
} from "../../../../../../hooks/queries/notes";
import { useModal } from "../../../../../../hooks/useModal";
import { noteService } from "../../../../../../services/noteService";
import { getLongFormattedDate } from "../../../../../../utils/dateFormatter";
import { mixpanelActions } from "../../../../../../utils/mixpanel";
import {
  CancelButton,
  Container,
  SubmitContainer,
  NoteContainer,
  DetailsContainer,
  Title,
  Label,
  ChildOneContainer,
  BackgroundContainer,
  LabelWeight,
} from "./DeleteNoteModal.styles";

type DeleteNoteModalProps = {
  noteId: number;
  setNotification: (
    notificationMessage: string,
    notificationType: "success" | "warning" | "error"
  ) => void;
  onClose: () => void;
};

export function DeleteNoteModal({
  noteId,
  setNotification,
  onClose,
}: DeleteNoteModalProps) {
  const { t } = useTranslation();

  const [noteContent, setNoteContent] = useState("");
  const [labelTimeContent, setLabelTimeContent] = useState("");
  const [creatorContent, setCreatorContent] = useState("");
  const { closeModal } = useModal();

  const { patientNoteData, isFetching, isError, refetch, isRefetching } =
    getNoteData(noteId);

  const isTask = patientNoteData?.noteType === noteTypes.TASK;
  // The record noun, lower case for mid-sentence use and capitalised for the title. Both come
  // from the bundle so the surrounding sentences below can be reordered by a translator.
  const recordLabel = isTask
    ? t("patientPage_notes_recordTask")
    : t("patientPage_notes_recordNote");
  const recordLabelCap = isTask
    ? t("patientPage_notesTable_deleteTask")
    : t("patientPage_notesTable_deleteNote");

  useEffect(() => {
    if (!isFetching && !isRefetching && patientNoteData) {
      setNoteContent(patientNoteData.notes);
      const timeStr = getLongFormattedDate(patientNoteData.time);
      setLabelTimeContent(timeStr);
      setCreatorContent(`${patientNoteData.reviewedByEmail}`);
    }
  }, [patientNoteData, isFetching, isRefetching]);

  const handleDeleteClick = () => {
    mixpanelActions.track(
      isTask ? "User Action: Delete Task" : "User Action: Delete Note"
    );

    noteService.deleteNote(noteId).then((deletedSuccessfully) => {
      const { data } = deletedSuccessfully;

      if (data) {
        closeModal();
        setNotification(
          t("patientPage_notes_deleted", { record: recordLabelCap }),
          "success"
        );
      } else {
        setNotification(
          t("patientPage_notes_deleteFailed", { record: recordLabel }),
          "error"
        );
      }
      onClose();
    });
  };

  const handleCancelClick = () => {
    closeModal();
  };

  const refreshData = () => {
    refetch();
  };

  if (isError) {
    return (
      <Container>
        <p>
          <Trans
            i18nKey="patientPage_notes_recordLoadError"
            values={{ record: recordLabel }}
            components={{
              retry: <RetryButton onClick={refreshData} />,
            }}
          />
        </p>
      </Container>
    );
  }

  return (
    <Container>
      <Title>
        {t("patientPage_notes_deleteTitle", { record: recordLabel })}
      </Title>
      <BackgroundContainer>
        <DetailsContainer>
          <ChildOneContainer>
            <LabelWeight>{t("patientPage_notesTable_dateCreated")}</LabelWeight>
            <Label>{labelTimeContent}</Label>
          </ChildOneContainer>
          <ChildOneContainer>
            <LabelWeight>{t("patientPage_notesTable_createdBy")}</LabelWeight>
            <Label>{creatorContent}</Label>
          </ChildOneContainer>
        </DetailsContainer>
        <NoteContainer>
          <LabelWeight>{recordLabelCap}</LabelWeight>
          <Label>{textTruncate(noteContent)}</Label>
        </NoteContainer>
      </BackgroundContainer>
      <SubmitContainer>
        <Button
          label={t("patientPage_notesTable_delete")}
          type="button"
          onClick={handleDeleteClick}
        />
        <CancelButton onClick={handleCancelClick}>
          {t("common_cancel")}
        </CancelButton>
      </SubmitContainer>
    </Container>
  );
}
